/**
 * CP7Privacy — privacy bottom sheet.
 * data-screen-label="cp-07 Privacy modal"
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../../lib/api-client';
import { useAuth } from '../../../store/auth';
import type { UseCoupleFlowReturn } from '../../../hooks/useCoupleFlow';
import styles from './Couple.module.css';

type Visibility = 'public' | 'friends' | 'private';

interface PrivacyOption {
  value: Visibility;
  labelKey: string;
  descKey: string;
}

const OPTIONS: PrivacyOption[] = [
  { value: 'public',  labelKey: 'couple.privacy.publicLabel',  descKey: 'couple.privacy.publicDesc' },
  { value: 'friends', labelKey: 'couple.privacy.friendsLabel', descKey: 'couple.privacy.friendsDesc' },
  { value: 'private', labelKey: 'couple.privacy.privateLabel', descKey: 'couple.privacy.privateDesc' },
];

interface CP7PrivacyProps {
  flow: UseCoupleFlowReturn;
  onClose: () => void;
}

export function CP7Privacy({ flow, onClose }: CP7PrivacyProps): JSX.Element {
  const { t } = useTranslation(['flows', 'common']);
  const jwt = useAuth((s) => s.jwt);
  const [visibility, setVisibility] = useState<Visibility>('public');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave(): Promise<void> {
    if (!jwt || !flow.couplePortraitId) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch(
        `/api/v1/couple-portrait/${flow.couplePortraitId}/privacy`,
        jwt,
        {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ visibility }),
        },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(body.error ?? t('common:errors.save'));
        return;
      }
      onClose();
    } catch {
      setError(t('common:errors.network'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {/* Backdrop */}
      <div className={styles.privacyBackdrop} onClick={onClose} />

      {/* Sheet */}
      <div className={styles.privacySheet} data-screen-label="cp-07 Privacy modal" role="dialog" aria-modal="true">
        <div className={styles.privacyHandle} />
        <h2 className={styles.privacyTitle}>{t('couple.privacy.title')}</h2>

        {OPTIONS.map((opt) => (
          <label key={opt.value} className={styles.radioRow}>
            <input
              type="radio"
              name="privacy"
              className={styles.radioInput}
              value={opt.value}
              checked={visibility === opt.value}
              onChange={() => setVisibility(opt.value)}
            />
            <div>
              <div className={styles.radioLabel}>{t(opt.labelKey)}</div>
              <div className={styles.radioDesc}>{t(opt.descKey)}</div>
            </div>
          </label>
        ))}

        {error && (
          <div
            role="alert"
            style={{
              marginTop: 10,
              fontSize: 'var(--type-ui-sm)',
              color: '#ffd6e0',
              textAlign: 'center',
            }}
          >
            {error}
          </div>
        )}

        {/* Save */}
        <button
          type="button"
          className={styles.ctaPrimary}
          style={{ marginTop: 20 }}
          onClick={() => void handleSave()}
          disabled={saving}
        >
          {saving ? <span className={styles.spinner} /> : null}
          {t('common:actions.save')}
        </button>

        {/* Cancel */}
        <button
          type="button"
          className={styles.ctaSecondary}
          onClick={onClose}
          disabled={saving}
        >
          {t('common:actions.cancel')}
        </button>
      </div>
    </>
  );
}
